/**
 * PAY-01B-04 + PAY-UNIFIED-02: Cashier Workspace
 *
 * Provides the complete cashier desk collection interface:
 * - Session management (open, view, close)
 * - Cash float, physical cash count, variance approval, bank deposit (PAY-02)
 * - Multi-invoice allocation for posted payments (PAY-02)
 * - Receipt number configuration
 * - Payment recording (CASH / CHEQUE / CARD / BANK_TRANSFER)
 * - PENDING payment queue with post + receipt issuance
 * - Session summary and reconciliation view
 * - Immutable audit trail per payment
 *
 * RBAC: finance.cashier.session.manage + finance.payment.cashier_collect
 * Backend: /api/finance/cashier/* (routes_pay01b_cashier.py)
 */

import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  ReceiptText,
  Wallet,
  ClipboardList,
  CheckCircle,
  XCircle,
  RefreshCw,
  Settings,
  Plus,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  AlertTriangle,
  ShieldCheck,
  History,
  Banknote,
  Calculator,
  Building,
  LinkIcon,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(amount) {
  if (!amount && amount !== 0) return "—";
  return `₹${parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function statusBadge(status) {
  const map = {
    OPEN: "bg-emerald-100 text-emerald-800",
    SUBMITTED: "bg-blue-100 text-blue-800",
    RECONCILED: "bg-purple-100 text-purple-800",
    DISCREPANCY: "bg-red-100 text-red-800",
    PENDING: "bg-amber-100 text-amber-800",
    POSTED: "bg-emerald-100 text-emerald-800",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${map[status] || "bg-gray-100 text-gray-700"}`}
    >
      {status}
    </span>
  );
}

// ─── Sub: Receipt Config Panel ─────────────────────────────────────────────
function ReceiptConfigPanel({ token, onClose }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ institution_code: "", format_template: "{CODE}-{FY}-{SEQ:06}", financial_year: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/api/finance/cashier/receipt-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setCfg(r.data);
        if (r.data.configured) {
          setForm({
            institution_code: r.data.institution_code,
            format_template: r.data.format_template,
            financial_year: r.data.financial_year,
          });
        } else {
          const y = new Date().getFullYear();
          const m = new Date().getMonth();
          const fy = m >= 3 ? `${y}-${String(y + 1).slice(-2)}` : `${y - 1}-${String(y).slice(-2)}`;
          setForm((f) => ({ ...f, financial_year: fy }));
        }
      })
      .catch(() => toast.error("Failed to load receipt config"));
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/finance/cashier/receipt-config`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Receipt config saved. Next: ${res.data.next_receipt_preview}`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      {cfg && !cfg.configured && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800">
          Receipt numbering is not yet configured. Set your institution code and format below.
        </div>
      )}
      {cfg?.configured && (
        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
          Last sequence: <strong>{cfg.last_sequence}</strong> &nbsp;·&nbsp; Next: <strong>{cfg.next_receipt_preview}</strong>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Institution Code</Label>
          <Input
            data-testid="receipt-institution-code"
            value={form.institution_code}
            onChange={(e) => setForm((f) => ({ ...f, institution_code: e.target.value.toUpperCase() }))}
            placeholder="e.g. VPC"
            maxLength={20}
          />
        </div>
        <div>
          <Label>Financial Year</Label>
          <Input
            data-testid="receipt-financial-year"
            value={form.financial_year}
            onChange={(e) => setForm((f) => ({ ...f, financial_year: e.target.value }))}
            placeholder="e.g. 2026-27"
            maxLength={10}
          />
        </div>
      </div>
      <div>
        <Label>Format Template</Label>
        <Input
          data-testid="receipt-format-template"
          value={form.format_template}
          onChange={(e) => setForm((f) => ({ ...f, format_template: e.target.value }))}
        />
        <p className="mt-1 text-xs text-gray-500">
          Placeholders: <code>{"{CODE}"}</code> <code>{"{FY}"}</code> <code>{"{SEQ:06}"}</code>
        </p>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="save-receipt-config-btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Configuration"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: Open Session Dialog ────────────────────────────────────────────────
function OpenSessionDialog({ token, onCreated, onClose }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ counter_id: "MAIN", session_date: today, opening_balance: "0" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/finance/cashier/sessions`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Cashier session opened");
      onCreated(res.data);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === "SESSION_ALREADY_OPEN") {
        toast.error(`Session already open for ${form.counter_id} on ${form.session_date}`);
      } else {
        toast.error(det?.message || "Failed to open session");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Counter ID</Label>
          <Input
            data-testid="open-session-counter-id"
            value={form.counter_id}
            onChange={(e) => setForm((f) => ({ ...f, counter_id: e.target.value }))}
            placeholder="e.g. MAIN, COUNTER-1"
          />
        </div>
        <div>
          <Label>Session Date</Label>
          <Input
            type="date"
            data-testid="open-session-date"
            value={form.session_date}
            onChange={(e) => setForm((f) => ({ ...f, session_date: e.target.value }))}
          />
        </div>
      </div>
      <div>
        <Label>Opening Balance (₹)</Label>
        <Input
          type="number"
          data-testid="open-session-opening-balance"
          value={form.opening_balance}
          onChange={(e) => setForm((f) => ({ ...f, opening_balance: e.target.value }))}
          min="0"
          step="0.01"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="open-session-submit-btn" onClick={submit} disabled={loading}>
          {loading ? "Opening…" : "Open Session"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: Close Session Dialog ───────────────────────────────────────────────
function CloseSessionDialog({ token, session, onClosed, onClose }) {
  const [closing, setClosing] = useState({ closing_balance: "0", notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.patch(
        `${API}/api/finance/cashier/sessions/${session.id}/close`,
        closing,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      if (d.status === "RECONCILED") {
        toast.success(`Session RECONCILED. Variance: ₹0`);
      } else {
        toast.warning(`Session ${d.status}. Variance: ${fmt(d.variance_amount)}`);
      }
      onClosed(d);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to close session");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      <div className="rounded-lg bg-gray-50 border p-3 text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-500">Counter</span>
          <span className="font-medium">{session.counter_id}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-gray-500">Session Date</span>
          <span className="font-medium">{session.session_date}</span>
        </div>
      </div>
      <div>
        <Label>Physical Cash Count (₹)</Label>
        <Input
          type="number"
          data-testid="close-session-closing-balance"
          value={closing.closing_balance}
          onChange={(e) => setClosing((f) => ({ ...f, closing_balance: e.target.value }))}
          min="0"
          step="0.01"
        />
        <p className="mt-1 text-xs text-gray-500">Enter the physically counted cash in the drawer.</p>
      </div>
      <div>
        <Label>Notes (optional)</Label>
        <Input
          data-testid="close-session-notes"
          value={closing.notes}
          onChange={(e) => setClosing((f) => ({ ...f, notes: e.target.value }))}
          placeholder="e.g. End of day reconciliation"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          data-testid="close-session-submit-btn"
          onClick={submit}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {loading ? "Closing…" : "Close Session"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: PAY-02 Shift Float Panel ────────────────────────────────────────────
function ShiftFloatPanel({ token, session, onUpdated }) {
  const [amount, setAmount] = useState(session.cash_float_amount || "");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) < 0) {
      toast.error("Enter a valid float amount (≥ 0)");
      return;
    }
    setLoading(true);
    try {
      await axios.patch(
        `${API}/api/finance/cashier/sessions/${session.id}/float?cash_float_amount=${amount}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cash float set to ₹${parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to set cash float");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 py-1" data-testid="shift-float-panel">
      <p className="text-sm text-gray-600">
        Record the physical cash float given to this cashier at session open.
        <br /><span className="text-xs text-amber-600 font-medium">INV-CASH-01: Float amount is the physical cash in drawer at start of shift.</span>
      </p>
      <div>
        <Label>Opening Cash Float (₹)</Label>
        <Input
          type="number"
          data-testid="shift-float-amount-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0"
          step="0.01"
          placeholder="0.00"
        />
      </div>
      <div className="flex justify-end">
        <Button data-testid="shift-float-submit-btn" onClick={submit} disabled={loading} size="sm">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Banknote className="h-4 w-4 mr-1" />}
          {loading ? "Saving…" : "Set Float"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: PAY-02 Cash Count + Variance ────────────────────────────────────────
function CashCountPanel({ token, session, onUpdated }) {
  const [counted, setCounted] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (counted === "" || parseFloat(counted) < 0) {
      toast.error("Enter the physical cash count");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ counted_cash_amount: counted });
      if (reason) params.append("variance_reason", reason);
      const res = await axios.patch(
        `${API}/api/finance/cashier/sessions/${session.id}/count?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = res.data;
      if (d.variance_amount && parseFloat(d.variance_amount) !== 0) {
        toast.warning(`Cash count recorded. Variance: ₹${parseFloat(d.variance_amount).toLocaleString("en-IN", { minimumFractionDigits: 2 })}. Supervisor approval required.`);
      } else {
        toast.success("Cash count recorded. No variance.");
      }
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to record cash count");
    } finally {
      setLoading(false);
    }
  };

  const expected = parseFloat(session.expected_balance || 0);
  const countedVal = parseFloat(counted || 0);
  const variance = isNaN(countedVal) ? null : countedVal - expected;

  return (
    <div className="space-y-3 py-1" data-testid="cash-count-panel">
      <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">Expected Balance</span>
          <span className="font-semibold">{fmt(expected)}</span>
        </div>
        {counted !== "" && variance !== null && (
          <div className="flex justify-between">
            <span className="text-gray-600">Variance</span>
            <span className={`font-semibold ${variance === 0 ? "text-emerald-700" : "text-red-600"}`}>
              {variance > 0 ? "+" : ""}{fmt(variance)}
            </span>
          </div>
        )}
      </div>
      <div>
        <Label>Physically Counted Cash (₹)</Label>
        <Input
          type="number"
          data-testid="cash-count-input"
          value={counted}
          onChange={(e) => setCounted(e.target.value)}
          min="0"
          step="0.01"
          placeholder="0.00"
        />
      </div>
      <div>
        <Label>Variance Reason (required if variance exists)</Label>
        <Input
          data-testid="cash-count-reason-input"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Short change error on collection #4"
        />
      </div>
      <div className="flex justify-end">
        <Button data-testid="cash-count-submit-btn" onClick={submit} disabled={loading} size="sm">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Calculator className="h-4 w-4 mr-1" />}
          {loading ? "Recording…" : "Record Count"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: PAY-02 Supervisor Variance Approval ─────────────────────────────────
function SupervisorApprovePanel({ token, session, onUpdated }) {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!notes.trim()) {
      toast.error("Approval notes are required");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/finance/cashier/sessions/${session.id}/supervisor-approve?approval_notes=${encodeURIComponent(notes)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Variance approved. Session marked RECONCILED.");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Approval failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 py-1" data-testid="supervisor-approve-panel">
      <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-gray-600">Variance Amount</span>
          <span className="font-semibold text-red-700">{fmt(session.variance_amount)}</span>
        </div>
        {session.variance_reason && (
          <div className="flex justify-between">
            <span className="text-gray-600">Cashier Reason</span>
            <span className="text-gray-800 text-xs max-w-[200px] text-right">{session.variance_reason}</span>
          </div>
        )}
      </div>
      <div>
        <Label>Supervisor Approval Notes <span className="text-red-500">*</span></Label>
        <Input
          data-testid="supervisor-approve-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Supervisor explanation for approving this variance"
        />
      </div>
      <div className="flex justify-end">
        <Button
          data-testid="supervisor-approve-submit-btn"
          onClick={submit}
          disabled={loading}
          size="sm"
          className="bg-amber-600 hover:bg-amber-700 text-white"
        >
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <ShieldCheck className="h-4 w-4 mr-1" />}
          {loading ? "Approving…" : "Approve Variance"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: PAY-02 Bank Deposit Recording ────────────────────────────────────────
function BankDepositPanel({ token, session, onUpdated }) {
  const [amount, setAmount] = useState("");
  const [ref, setRef] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error("Enter a valid deposit amount");
      return;
    }
    if (!ref.trim()) {
      toast.error("Bank deposit reference is required");
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ bank_deposit_amount: amount, bank_deposit_reference: ref });
      await axios.patch(
        `${API}/api/finance/cashier/sessions/${session.id}/bank-deposit?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Bank deposit recorded.");
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Failed to record bank deposit");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3 py-1" data-testid="bank-deposit-panel">
      <p className="text-sm text-gray-600">Record the bank deposit slip reference for today's cash collections.</p>
      <div>
        <Label>Deposit Amount (₹)</Label>
        <Input
          type="number"
          data-testid="bank-deposit-amount-input"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          min="0.01"
          step="0.01"
          placeholder="0.00"
        />
      </div>
      <div>
        <Label>Bank Deposit Reference <span className="text-red-500">*</span></Label>
        <Input
          data-testid="bank-deposit-ref-input"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="e.g. HDFC-DEP-20241031-001"
        />
      </div>
      <div className="flex justify-end">
        <Button data-testid="bank-deposit-submit-btn" onClick={submit} disabled={loading} size="sm">
          {loading ? <RefreshCw className="h-4 w-4 animate-spin mr-1" /> : <Building className="h-4 w-4 mr-1" />}
          {loading ? "Recording…" : "Record Deposit"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: PAY-02 Invoice Allocation Dialog ─────────────────────────────────────
function AllocateInvoicesDialog({ token, paymentId, paymentAmount, onAllocated, onClose }) {
  const [outstanding, setOutstanding] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // We'll fetch from payment detail to get student_account_id, then get outstanding
    const load = async () => {
      setLoading(true);
      try {
        // Get payment audit to find student account
        const auditRes = await axios.get(`${API}/api/finance/cashier/payments/${paymentId}/audit`,
          { headers: { Authorization: `Bearer ${token}` } });
        const studentId = auditRes.data?.student_account_id;
        if (studentId) {
          const outRes = await axios.get(
            `${API}/api/finance/cashier/student/${studentId}/outstanding`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setOutstanding(outRes.data?.invoices || []);
        }
      } catch {
        toast.error("Failed to load outstanding invoices");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, paymentId]);

  const toggleInvoice = (invId) => {
    setAllocations((prev) => {
      const exists = prev.find((a) => a.invoice_id === invId);
      if (exists) return prev.filter((a) => a.invoice_id !== invId);
      const inv = outstanding.find((i) => i.invoice_id === invId);
      return [...prev, { invoice_id: invId, amount: inv?.outstanding_amount || "0" }];
    });
  };

  const setAllocAmt = (invId, val) => {
    setAllocations((prev) => prev.map((a) => a.invoice_id === invId ? { ...a, amount: val } : a));
  };

  const totalAlloc = allocations.reduce((s, a) => s + parseFloat(a.amount || 0), 0);

  const submit = async () => {
    if (allocations.length === 0) { toast.error("Select at least one invoice"); return; }
    if (totalAlloc > parseFloat(paymentAmount)) { toast.error("Total allocation exceeds payment amount"); return; }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/finance/cashier/payments/${paymentId}/allocate`,
        { allocations: allocations.map((a) => ({ invoice_id: a.invoice_id, amount: parseFloat(a.amount) })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Allocated to ${allocations.length} invoice(s)`);
      onAllocated();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Allocation failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 py-1 max-h-[60vh] overflow-y-auto" data-testid="allocate-invoices-dialog">
      <div className="flex justify-between text-sm font-medium">
        <span>Payment Amount:</span>
        <span className="text-emerald-700">{fmt(paymentAmount)}</span>
      </div>
      {loading ? (
        <div className="text-center py-6 text-gray-500 text-sm">
          <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
          Loading outstanding invoices…
        </div>
      ) : outstanding.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No outstanding invoices found for this student.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead className="text-right">Outstanding</TableHead>
              <TableHead className="text-right">Allocate (₹)</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {outstanding.map((inv) => {
              const alloc = allocations.find((a) => a.invoice_id === inv.invoice_id);
              return (
                <TableRow key={inv.invoice_id}>
                  <TableCell>
                    <input
                      type="checkbox"
                      data-testid={`alloc-inv-check-${inv.invoice_id}`}
                      checked={!!alloc}
                      onChange={() => toggleInvoice(inv.invoice_id)}
                      className="rounded"
                    />
                  </TableCell>
                  <TableCell className="text-xs">
                    <div className="font-medium">{inv.invoice_number}</div>
                    <div className="text-gray-500">{inv.description || "—"}</div>
                  </TableCell>
                  <TableCell className="text-right text-sm">{fmt(inv.outstanding_amount)}</TableCell>
                  <TableCell className="text-right">
                    {alloc ? (
                      <Input
                        type="number"
                        data-testid={`alloc-inv-amount-${inv.invoice_id}`}
                        value={alloc.amount}
                        onChange={(e) => setAllocAmt(inv.invoice_id, e.target.value)}
                        className="w-24 text-right text-sm h-7"
                        min="0.01"
                        max={inv.outstanding_amount}
                        step="0.01"
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      <div className="flex justify-between items-center pt-2 border-t">
        <div className="text-sm">
          <span className="text-gray-600">Total Allocated: </span>
          <span className={`font-semibold ${totalAlloc > parseFloat(paymentAmount) ? "text-red-600" : "text-emerald-700"}`}>
            {fmt(totalAlloc)}
          </span>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} size="sm">Cancel</Button>
          <Button
            data-testid="allocate-submit-btn"
            onClick={submit}
            disabled={submitting || allocations.length === 0}
            size="sm"
          >
            {submitting ? <RefreshCw className="h-3 w-3 animate-spin mr-1" /> : <LinkIcon className="h-3 w-3 mr-1" />}
            {submitting ? "Allocating…" : "Apply Allocation"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Sub: Collect Payment Dialog ─────────────────────────────────────────────
function CollectPaymentDialog({ token, sessionId, onCollected, onClose }) {
  const [studentAccounts, setStudentAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [form, setForm] = useState({
    student_account_id: "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "CASH",
    payment_amount: "",
    currency_id: "",
    reference_number: "",
    collection_notes: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/api/finance/admin/student-accounts?limit=100`, { headers: { Authorization: `Bearer ${token}` } }),
      axios.get(`${API}/api/finance/admin/currencies`, { headers: { Authorization: `Bearer ${token}` } }),
    ])
      .then(([saRes, curRes]) => {
        setStudentAccounts(saRes.data?.accounts || []);
        const curs = curRes.data?.items || curRes.data?.currencies || [];
        setCurrencies(curs);
        if (curs.length > 0) setForm((f) => ({ ...f, currency_id: curs[0].id }));
      })
      .catch(() => {});
  }, [token]);

  const submit = async () => {
    if (!form.student_account_id || !form.payment_amount || !form.currency_id) {
      toast.error("Please fill all required fields");
      return;
    }
    setLoading(true);
    const payload = { ...form };
    if (sessionId) payload.cashier_session_id = sessionId;
    if (!payload.reference_number) delete payload.reference_number;
    if (!payload.collection_notes) delete payload.collection_notes;
    try {
      const res = await axios.post(`${API}/api/finance/cashier/collect`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Payment recorded PENDING — ${res.data.payment_id?.slice(0, 8)}…`);
      onCollected(res.data);
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det?.code === "DUPLICATE_PAYMENT_DETECTED") {
        toast.error(`Duplicate detected: payment ${det.existing_payment_id?.slice(0, 8)} already pending`);
      } else {
        toast.error(det?.message || "Collection failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 py-2">
      <div>
        <Label>Student Account <span className="text-red-500">*</span></Label>
        <Select
          value={form.student_account_id}
          onValueChange={(v) => setForm((f) => ({ ...f, student_account_id: v }))}
        >
          <SelectTrigger data-testid="collect-student-account-select">
            <SelectValue placeholder="Select student account" />
          </SelectTrigger>
          <SelectContent>
            {studentAccounts.map((sa) => (
              <SelectItem key={sa.id} value={sa.id}>
                {sa.student_number || sa.id.slice(0, 8)} — {sa.student_name || ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Payment Date <span className="text-red-500">*</span></Label>
          <Input
            type="date"
            data-testid="collect-payment-date"
            value={form.payment_date}
            onChange={(e) => setForm((f) => ({ ...f, payment_date: e.target.value }))}
          />
        </div>
        <div>
          <Label>Method <span className="text-red-500">*</span></Label>
          <Select
            value={form.payment_method}
            onValueChange={(v) => setForm((f) => ({ ...f, payment_method: v }))}
          >
            <SelectTrigger data-testid="collect-payment-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="CASH">Cash</SelectItem>
              <SelectItem value="CHEQUE">Cheque</SelectItem>
              <SelectItem value="CARD">Card (POS)</SelectItem>
              <SelectItem value="BANK_TRANSFER">Bank Transfer / NEFT</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Amount <span className="text-red-500">*</span></Label>
          <Input
            type="number"
            data-testid="collect-payment-amount"
            value={form.payment_amount}
            onChange={(e) => setForm((f) => ({ ...f, payment_amount: e.target.value }))}
            min="0.01"
            step="0.01"
            placeholder="0.00"
          />
        </div>
        <div>
          <Label>Currency <span className="text-red-500">*</span></Label>
          <Select
            value={form.currency_id}
            onValueChange={(v) => setForm((f) => ({ ...f, currency_id: v }))}
          >
            <SelectTrigger data-testid="collect-currency-select">
              <SelectValue placeholder="Currency" />
            </SelectTrigger>
            <SelectContent>
              {currencies.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.iso_code} — {c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Reference Number {(form.payment_method === "CHEQUE" || form.payment_method === "BANK_TRANSFER") ? <span className="text-red-500">*</span> : <span className="text-gray-400">(recommended)</span>}</Label>
        <Input
          data-testid="collect-reference-number"
          value={form.reference_number}
          onChange={(e) => setForm((f) => ({ ...f, reference_number: e.target.value }))}
          placeholder={form.payment_method === "CHEQUE" ? "Cheque number" : form.payment_method === "BANK_TRANSFER" ? "UTR number" : "Optional"}
        />
      </div>
      <div>
        <Label>Notes</Label>
        <Input
          data-testid="collect-notes"
          value={form.collection_notes}
          onChange={(e) => setForm((f) => ({ ...f, collection_notes: e.target.value }))}
          placeholder="e.g. Semester 1 tuition fee"
        />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button data-testid="collect-submit-btn" onClick={submit} disabled={loading}>
          {loading ? "Recording…" : "Record Payment"}
        </Button>
      </div>
    </div>
  );
}

// ─── Sub: Audit Trail Dialog ──────────────────────────────────────────────────
function AuditTrailDialog({ token, paymentId, onClose }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/api/finance/cashier/payments/${paymentId}/audit`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setEvents(r.data.events || []))
      .catch(() => toast.error("Failed to load audit trail"))
      .finally(() => setLoading(false));
  }, [token, paymentId]);

  const eventColor = {
    PAYMENT_RECORDED: "bg-blue-100 text-blue-800",
    PAYMENT_POSTED: "bg-emerald-100 text-emerald-800",
    RECEIPT_ISSUED: "bg-purple-100 text-purple-800",
    COLLECTION_VERIFIED: "bg-teal-100 text-teal-800",
    PAYMENT_REVERSED: "bg-red-100 text-red-800",
    DUPLICATE_DETECTED: "bg-amber-100 text-amber-800",
  };

  return (
    <div className="space-y-3 py-1 max-h-96 overflow-y-auto">
      {loading && <p className="text-sm text-gray-500">Loading audit trail…</p>}
      {!loading && events.length === 0 && (
        <p className="text-sm text-gray-500">No audit events found.</p>
      )}
      {events.map((e, i) => (
        <div key={e.id} className="flex gap-3">
          <div className="flex flex-col items-center">
            <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5" />
            {i < events.length - 1 && <div className="w-0.5 flex-1 bg-gray-200" />}
          </div>
          <div className="pb-3 flex-1">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${eventColor[e.event_type] || "bg-gray-100 text-gray-700"}`}>
              {e.event_type}
            </span>
            {e.before_status && (
              <span className="ml-2 text-xs text-gray-500">
                {e.before_status} <ArrowRight className="inline h-3 w-3" /> {e.after_status}
              </span>
            )}
            {e.financial_impact && (
              <span className="ml-2 text-xs font-medium text-gray-700">{fmt(e.financial_impact)}</span>
            )}
            {e.notes && <p className="mt-0.5 text-xs text-gray-500">{e.notes}</p>}
            <p className="mt-0.5 text-xs text-gray-400">
              {e.created_at ? new Date(e.created_at).toLocaleString("en-IN") : "—"}
            </p>
          </div>
        </div>
      ))}
      <div className="flex justify-end pt-2">
        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CashierWorkspace() {
  const { user, token } = useAuth();
  const [tab, setTab] = useState("sessions"); // sessions | queue | config
  const [sessions, setSessions] = useState([]);
  const [pendingPayments, setPendingPayments] = useState([]);
  const [activeSession, setActiveSession] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [posting, setPosting] = useState(null); // payment_id being posted

  // Dialogs
  const [dialog, setDialog] = useState(null); // "open-session" | "close-session" | "collect" | "audit" | "config" | "shift-float" | "shift-count" | "shift-supervisor" | "shift-bankdeposit" | "allocate"
  const [auditPaymentId, setAuditPaymentId] = useState(null);
  const [closeSessionTarget, setCloseSessionTarget] = useState(null);
  const [allocatePaymentTarget, setAllocatePaymentTarget] = useState(null);

  const isManager = ["finance_manager", "cfo", "institution_admin", "super_admin"].includes(user?.role);
  const isOfficer = ["finance_officer", "finance_manager", "cfo", "institution_admin", "super_admin"].includes(user?.role);

  const loadSessions = useCallback(async () => {
    setLoadingSessions(true);
    try {
      const res = await axios.get(`${API}/api/finance/cashier/sessions?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.sessions || [];
      setSessions(list);
      const open = list.find((s) => s.status === "OPEN");
      setActiveSession(open || null);
    } catch {
      toast.error("Failed to load cashier sessions");
    } finally {
      setLoadingSessions(false);
    }
  }, [token]);

  const loadQueue = useCallback(async () => {
    setLoadingQueue(true);
    try {
      const res = await axios.get(`${API}/api/finance/cashier/pending-payments?limit=50`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPendingPayments(res.data.payments || []);
    } catch {
      toast.error("Failed to load pending payments");
    } finally {
      setLoadingQueue(false);
    }
  }, [token]);

  useEffect(() => {
    loadSessions();
    loadQueue();
  }, [loadSessions, loadQueue]);

  const handlePostPayment = async (paymentId) => {
    setPosting(paymentId);
    try {
      const res = await axios.patch(
        `${API}/api/finance/cashier/payments/${paymentId}/post`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Posted! Receipt: ${res.data.receipt_number}`);
      loadQueue();
      loadSessions();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message || "Post failed");
    } finally {
      setPosting(null);
    }
  };

  const methodLabel = { CASH: "Cash", CHEQUE: "Cheque", CARD: "Card", BANK_TRANSFER: "Bank Transfer", ONLINE: "Online", INTERNAL: "Internal" };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Wallet className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="text-xl font-semibold text-gray-900">Cashier Workspace</p>
              <p className="text-sm text-gray-500">Manual collections · Receipt engine · PAY-01B + PAY-02</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSession && (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="font-medium text-emerald-800">Session Open</span>
                <span className="text-emerald-600">· {activeSession.counter_id}</span>
              </div>
            )}
            {isManager && (
              <Button
                variant="outline"
                size="sm"
                data-testid="open-receipt-config-btn"
                onClick={() => setDialog("config")}
              >
                <Settings className="h-4 w-4 mr-1" />
                Receipt Config
              </Button>
            )}
            {isOfficer && !activeSession && (
              <Button
                size="sm"
                data-testid="open-session-btn"
                onClick={() => setDialog("open-session")}
              >
                <Plus className="h-4 w-4 mr-1" />
                Open Session
              </Button>
            )}
            {activeSession && isOfficer && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="close-session-btn"
                  onClick={() => { setCloseSessionTarget(activeSession); setDialog("close-session"); }}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  Close Session
                </Button>
                <Button
                  size="sm"
                  data-testid="collect-payment-btn"
                  onClick={() => setDialog("collect")}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Collect Payment
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b px-6">
        <div className="flex gap-0">
          {[
            { key: "sessions", label: "Sessions", icon: ClipboardList, testid: "cashier-tab-sessions" },
            { key: "queue", label: "Pending Queue", icon: AlertTriangle, testid: "cashier-tab-queue", badge: pendingPayments.length || null },
            { key: "config", label: "Receipt Config", icon: Settings, testid: "cashier-tab-config", hidden: !isManager },
          ].filter((t) => !t.hidden).map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                tab === t.key
                  ? "border-blue-600 text-blue-700"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
              {t.badge ? (
                <span className="rounded-full bg-amber-100 text-amber-800 text-xs px-1.5 py-0.5 font-semibold">
                  {t.badge}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-4">
        {/* ─── Sessions Tab ─────────────────────────────────────────────────── */}
        {tab === "sessions" && (
          <div data-testid="cashier-sessions-panel">
            {/* PAY-02 Active Session Controls */}
            {activeSession && (
              <Card className="mb-4 border-emerald-200 bg-emerald-50/40">
                <CardHeader className="pb-2 pt-4 px-4">
                  <CardTitle className="text-sm font-semibold text-emerald-800 flex items-center gap-2">
                    <Banknote className="h-4 w-4" />
                    Active Session Controls — {activeSession.counter_id} · {activeSession.session_date}
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="rounded-lg bg-white border p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Float Set</div>
                      <div className="font-semibold text-sm">{activeSession.cash_float_amount ? fmt(activeSession.cash_float_amount) : <span className="text-amber-600">Not set</span>}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs h-7"
                        data-testid="set-float-btn"
                        onClick={() => setDialog("shift-float")}
                      >
                        <Banknote className="h-3 w-3 mr-1" />Set Float
                      </Button>
                    </div>
                    <div className="rounded-lg bg-white border p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Cash Count</div>
                      <div className="font-semibold text-sm">{activeSession.counted_cash_amount ? fmt(activeSession.counted_cash_amount) : <span className="text-gray-400">—</span>}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs h-7"
                        data-testid="cash-count-btn"
                        onClick={() => setDialog("shift-count")}
                      >
                        <Calculator className="h-3 w-3 mr-1" />Count Cash
                      </Button>
                    </div>
                    <div className="rounded-lg bg-white border p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Variance</div>
                      <div className={`font-semibold text-sm ${activeSession.variance_amount && parseFloat(activeSession.variance_amount) !== 0 ? "text-red-600" : "text-emerald-600"}`}>
                        {activeSession.variance_amount ? fmt(activeSession.variance_amount) : "—"}
                      </div>
                      {isManager && activeSession.variance_amount && parseFloat(activeSession.variance_amount) !== 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="mt-2 w-full text-xs h-7 border-amber-300 text-amber-700"
                          data-testid="supervisor-approve-btn"
                          onClick={() => setDialog("shift-supervisor")}
                        >
                          <ShieldCheck className="h-3 w-3 mr-1" />Approve
                        </Button>
                      )}
                    </div>
                    <div className="rounded-lg bg-white border p-3 text-center">
                      <div className="text-xs text-gray-500 mb-1">Bank Deposit</div>
                      <div className="font-semibold text-sm text-xs">{activeSession.bank_deposit_reference || <span className="text-gray-400">Not recorded</span>}</div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="mt-2 w-full text-xs h-7"
                        data-testid="bank-deposit-btn"
                        onClick={() => setDialog("shift-bankdeposit")}
                      >
                        <Building className="h-3 w-3 mr-1" />Record Deposit
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-800">Cashier Sessions</h2>
              <Button variant="ghost" size="sm" onClick={loadSessions} data-testid="refresh-sessions-btn">
                <RefreshCw className={`h-4 w-4 ${loadingSessions ? "animate-spin" : ""}`} />
              </Button>
            </div>
            {sessions.length === 0 && !loadingSessions && (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <Wallet className="h-8 w-8 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">No sessions found</p>
                  <p className="text-sm mt-1">Open a cashier session to start collecting payments.</p>
                  {isOfficer && (
                    <Button className="mt-4" size="sm" onClick={() => setDialog("open-session")} data-testid="open-session-empty-btn">
                      Open Session
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
            {sessions.length > 0 && (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Counter</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Opening</TableHead>
                      <TableHead className="text-right">Expected</TableHead>
                      <TableHead className="text-right">Closing</TableHead>
                      <TableHead className="text-right">Variance</TableHead>
                      <TableHead className="text-right">Payments</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sessions.map((s) => (
                      <TableRow key={s.id} data-testid={`session-row-${s.id}`}>
                        <TableCell className="font-medium">{s.session_date}</TableCell>
                        <TableCell>{s.counter_id}</TableCell>
                        <TableCell>{statusBadge(s.status)}</TableCell>
                        <TableCell className="text-right">{fmt(s.opening_balance)}</TableCell>
                        <TableCell className="text-right">{s.expected_balance ? fmt(s.expected_balance) : "—"}</TableCell>
                        <TableCell className="text-right">{s.closing_balance ? fmt(s.closing_balance) : "—"}</TableCell>
                        <TableCell className="text-right">
                          {s.variance_amount ? (
                            <span className={parseFloat(s.variance_amount) === 0 ? "text-emerald-600" : "text-red-600"}>
                              {fmt(s.variance_amount)}
                            </span>
                          ) : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="rounded-full bg-gray-100 text-gray-700 text-xs px-2 py-0.5">{s.payment_count}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {/* ─── Pending Queue Tab ────────────────────────────────────────────── */}
        {tab === "queue" && (
          <div data-testid="cashier-pending-queue-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h2 className="text-base font-semibold text-gray-800">Pending Payment Queue</h2>
                <p className="text-sm text-gray-500">Post payments to finalize and issue receipts. Requires <code className="text-xs bg-gray-100 px-1 rounded">finance.payment.reconcile</code>.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={loadQueue} data-testid="refresh-queue-btn">
                <RefreshCw className={`h-4 w-4 ${loadingQueue ? "animate-spin" : ""}`} />
              </Button>
            </div>
            {pendingPayments.length === 0 && !loadingQueue && (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <CheckCircle className="h-8 w-8 mx-auto mb-3 text-emerald-300" />
                  <p className="font-medium">All clear</p>
                  <p className="text-sm mt-1">No pending payments awaiting posting.</p>
                </CardContent>
              </Card>
            )}
            {pendingPayments.length > 0 && (
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingPayments.map((p) => (
                      <TableRow key={p.id} data-testid={`pending-payment-row-${p.id}`}>
                        <TableCell>
                          <div className="font-medium text-sm">{p.student_number || "—"}</div>
                          <div className="text-xs text-gray-500">{p.student_name || "—"}</div>
                        </TableCell>
                        <TableCell className="text-sm">{p.payment_date}</TableCell>
                        <TableCell>
                          <span className="text-xs font-medium">{methodLabel[p.payment_method] || p.payment_method}</span>
                        </TableCell>
                        <TableCell className="text-right font-semibold">{fmt(p.payment_amount)}</TableCell>
                        <TableCell className="text-xs text-gray-600">{p.reference_number || "—"}</TableCell>
                        <TableCell className="text-xs text-gray-500 max-w-[120px] truncate">{p.collection_notes || "—"}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {isManager && (
                              <Button
                                size="sm"
                                variant="outline"
                                data-testid={`post-payment-btn-${p.id}`}
                                disabled={posting === p.id}
                                onClick={() => handlePostPayment(p.id)}
                                className="text-xs h-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                              >
                                {posting === p.id ? (
                                  <RefreshCw className="h-3 w-3 animate-spin" />
                                ) : (
                                  <><CheckCircle className="h-3 w-3 mr-1" />Post</>
                                )}
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`allocate-payment-btn-${p.id}`}
                              onClick={() => { setAllocatePaymentTarget(p); setDialog("allocate"); }}
                              className="text-xs h-7 border-blue-200 border hover:bg-blue-50 text-blue-600"
                              title="Allocate to invoices"
                            >
                              <LinkIcon className="h-3 w-3 mr-1" />Allocate
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`view-audit-btn-${p.id}`}
                              onClick={() => { setAuditPaymentId(p.id); setDialog("audit"); }}
                              className="text-xs h-7"
                            >
                              <History className="h-3 w-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            )}
          </div>
        )}

        {/* ─── Receipt Config Tab ───────────────────────────────────────────── */}
        {tab === "config" && isManager && (
          <div data-testid="cashier-config-panel">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Receipt Number Configuration</CardTitle>
              </CardHeader>
              <CardContent>
                <ReceiptConfigPanel token={token} onClose={() => setTab("sessions")} />
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* ─── Dialogs ─────────────────────────────────────────────────────────── */}
      <Dialog open={dialog === "open-session"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Open Cashier Session</DialogTitle></DialogHeader>
          <OpenSessionDialog
            token={token}
            onCreated={(s) => { loadSessions(); setDialog(null); toast.success(`Session ${s.session_id?.slice(0, 8)} opened`); }}
            onClose={() => setDialog(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "close-session"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close Cashier Session</DialogTitle></DialogHeader>
          {closeSessionTarget && (
            <CloseSessionDialog
              token={token}
              session={closeSessionTarget}
              onClosed={() => { loadSessions(); loadQueue(); setDialog(null); }}
              onClose={() => setDialog(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "collect"} onOpenChange={() => setDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ReceiptText className="h-5 w-5" />
              Record Payment
            </DialogTitle>
          </DialogHeader>
          <CollectPaymentDialog
            token={token}
            sessionId={activeSession?.id}
            onCollected={() => { loadQueue(); setDialog(null); }}
            onClose={() => setDialog(null)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "config"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Receipt Number Configuration
            </DialogTitle>
          </DialogHeader>
          <ReceiptConfigPanel token={token} onClose={() => setDialog(null)} />
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "audit"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Payment Audit Trail
            </DialogTitle>
          </DialogHeader>
          {auditPaymentId && (
            <AuditTrailDialog
              token={token}
              paymentId={auditPaymentId}
              onClose={() => setDialog(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* PAY-02 Shift Dialogs */}
      <Dialog open={dialog === "shift-float"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5" />Set Cash Float</DialogTitle></DialogHeader>
          {activeSession && <ShiftFloatPanel token={token} session={activeSession} onUpdated={() => { loadSessions(); setDialog(null); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "shift-count"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Calculator className="h-5 w-5" />Record Cash Count</DialogTitle></DialogHeader>
          {activeSession && <CashCountPanel token={token} session={activeSession} onUpdated={() => { loadSessions(); setDialog(null); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "shift-supervisor"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-600" />Supervisor Variance Approval</DialogTitle></DialogHeader>
          {activeSession && <SupervisorApprovePanel token={token} session={activeSession} onUpdated={() => { loadSessions(); setDialog(null); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "shift-bankdeposit"} onOpenChange={() => setDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Building className="h-5 w-5" />Record Bank Deposit</DialogTitle></DialogHeader>
          {activeSession && <BankDepositPanel token={token} session={activeSession} onUpdated={() => { loadSessions(); setDialog(null); }} />}
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "allocate"} onOpenChange={() => { setDialog(null); setAllocatePaymentTarget(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><LinkIcon className="h-5 w-5" />Allocate Payment to Invoices</DialogTitle></DialogHeader>
          {allocatePaymentTarget && (
            <AllocateInvoicesDialog
              token={token}
              paymentId={allocatePaymentTarget.id}
              paymentAmount={allocatePaymentTarget.payment_amount}
              onAllocated={() => { loadQueue(); setDialog(null); setAllocatePaymentTarget(null); }}
              onClose={() => { setDialog(null); setAllocatePaymentTarget(null); }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
